/**
 * Utility functions for URL validation and processing
 */

/**
 * Validates and cleans social media URLs to prevent malformed URLs
 * @param url - The URL to validate and clean
 * @param platform - The social media platform (twitter, facebook, instagram)
 * @returns A clean, valid URL or null if invalid
 */
export function validateSocialMediaUrl(
  url: string | null | undefined,
  platform: 'twitter' | 'facebook' | 'instagram' | 'tiktok'
): string | null {
  if (!url || typeof url !== 'string') return null

  // Remove any whitespace
  const cleanUrl = url.trim()
  if (!cleanUrl) return null

  try {
    // If it's already a full URL, validate it
    if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
      const urlObj = new URL(cleanUrl)

      // Validate the domain matches the platform
      switch (platform) {
        case 'twitter':
          if (
            !urlObj.hostname.includes('twitter.com') &&
            !urlObj.hostname.includes('x.com')
          ) {
            return null
          }
          break
        case 'facebook':
          if (!urlObj.hostname.includes('facebook.com')) {
            return null
          }
          break
        case 'instagram':
          if (!urlObj.hostname.includes('instagram.com')) {
            return null
          }
          break
        case 'tiktok':
          if (!urlObj.hostname.includes('tiktok.com')) {
            return null
          }
          break
      }

      return cleanUrl
    }

    // If it's just a username/handle, construct the proper URL
    let username = cleanUrl

    // Remove @ symbol if present
    if (username.startsWith('@')) {
      username = username.substring(1)
    }

    // Remove any leading slashes
    username = username.replace(/^\/+/, '')

    // Remove any trailing slashes
    username = username.replace(/\/+$/, '')

    if (!username) return null

    // Construct the proper URL based on platform
    switch (platform) {
      case 'twitter':
        return `https://twitter.com/${username}`
      case 'facebook':
        return `https://facebook.com/${username}`
      case 'instagram':
        return `https://instagram.com/${username}`
      case 'tiktok':
        return `https://tiktok.com/@${username.replace(/^@/, '')}`
      default:
        return null
    }
  } catch (error) {
    // If URL construction fails, return null
    return null
  }
}

/**
 * Validates a general URL
 * @param url - The URL to validate
 * @returns A valid URL or null if invalid
 */
export function validateUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') return null

  const cleanUrl = url.trim()
  if (!cleanUrl) return null

  try {
    // If it doesn't start with a protocol, add https://
    const urlToValidate =
      cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')
        ? cleanUrl
        : `https://${cleanUrl}`

    new URL(urlToValidate)
    return urlToValidate
  } catch (error) {
    return null
  }
}
